import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './SharedEditorialsPage.css';

export default function SharedEditorialsPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { currentUser, isAdmin } = useAuth();

  const [items, setItems] = useState([]);
  const [lookbookItems, setLookbookItems] = useState([]);
  const [adCreatives, setAdCreatives] = useState([]);
  const [liveNewArrivalItems, setLiveNewArrivalItems] = useState([]);
  const [scheduledNewProductItems, setScheduledNewProductItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lookbookLoading, setLookbookLoading] = useState(false);
  const [adCreativesLoading, setAdCreativesLoading] = useState(false);
  const [liveNewArrivalsLoading, setLiveNewArrivalsLoading] = useState(false);
  const [scheduledNewProductsLoading, setScheduledNewProductsLoading] = useState(false);
  const [uploadingAdCreatives, setUploadingAdCreatives] = useState(false);
  const [editorialsPage, setEditorialsPage] = useState(1);
  const [lookbookPage, setLookbookPage] = useState(1);
  const [liveNewArrivalsPage, setLiveNewArrivalsPage] = useState(1);
  const [scheduledNewProductsPage, setScheduledNewProductsPage] = useState(1);
  const [editorialsSkuInput, setEditorialsSkuInput] = useState('');
  const [editorialsSkuSearch, setEditorialsSkuSearch] = useState('');
  const [lookbookSkuInput, setLookbookSkuInput] = useState('');
  const [lookbookSkuSearch, setLookbookSkuSearch] = useState('');
  const [liveNewArrivalsSkuInput, setLiveNewArrivalsSkuInput] = useState('');
  const [liveNewArrivalsSkuSearch, setLiveNewArrivalsSkuSearch] = useState('');
  const [scheduledNewProductsSkuInput, setScheduledNewProductsSkuInput] = useState('');
  const [scheduledNewProductsSkuSearch, setScheduledNewProductsSkuSearch] = useState('');
  const [editorialsPagination, setEditorialsPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 1,
    hasPrev: false,
    hasNext: false,
  });
  const [lookbookPagination, setLookbookPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 1,
    hasPrev: false,
    hasNext: false,
  });
  const [liveNewArrivalsPagination, setLiveNewArrivalsPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 1,
    hasPrev: false,
    hasNext: false,
  });
  const [scheduledNewProductsPagination, setScheduledNewProductsPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 1,
    hasPrev: false,
    hasNext: false,
  });
  const [error, setError] = useState('');
  const [lookbookError, setLookbookError] = useState('');
  const [adCreativesError, setAdCreativesError] = useState('');
  const [liveNewArrivalsError, setLiveNewArrivalsError] = useState('');
  const [scheduledNewProductsError, setScheduledNewProductsError] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [copiedProductUrlId, setCopiedProductUrlId] = useState(null);
  const [copiedAdCreativeId, setCopiedAdCreativeId] = useState(null);
  const [deletingAdCreativeId, setDeletingAdCreativeId] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [liveProductLightbox, setLiveProductLightbox] = useState(null);
  const [activeTab, setActiveTab] = useState('editorials');
  const [activeNewProductsTab, setActiveNewProductsTab] = useState('liveNewArrivals');
  const [loadedKeys, setLoadedKeys] = useState({});

  const fetchEditorials = useCallback(async () => {
    if (!token) {
      setError('Unauthorized: Access token is missing in the URL query parameters.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        token,
        page: String(editorialsPage),
        limit: '100',
      });
      if (editorialsSkuSearch) params.set('sku', editorialsSkuSearch);
      const requestKey = params.toString();
      const res = await fetch(`/api/v1/public/lookbook-v2/editorials?${params.toString()}`);
      if (res.status === 401 || res.status === 403) {
        throw new Error('Access Denied: The provided token is invalid or has expired.');
      }
      if (!res.ok) {
        throw new Error('Failed to load editorials from the catalog.');
      }
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to load editorials.');
      }
      setItems(data.items || []);
      setEditorialsPagination({
        page: data.page || editorialsPage,
        limit: data.limit || 100,
        total: data.total || 0,
        totalPages: data.totalPages || 1,
        hasPrev: Boolean(data.hasPrev),
        hasNext: Boolean(data.hasNext),
      });
      setLoadedKeys((prev) => ({ ...prev, editorials: requestKey }));
    } catch (err) {
      setError(err.message || 'An unexpected error occurred while loading.');
    } finally {
      setLoading(false);
    }
  }, [token, editorialsPage, editorialsSkuSearch]);

  const fetchLookbookImages = useCallback(async () => {
    if (!token) {
      setLookbookError('Unauthorized: Access token is missing in the URL query parameters.');
      return;
    }

    setLookbookLoading(true);
    setLookbookError('');
    try {
      const params = new URLSearchParams({
        token,
        page: String(lookbookPage),
        limit: '100',
      });
      if (lookbookSkuSearch) params.set('sku', lookbookSkuSearch);
      const requestKey = params.toString();
      const res = await fetch(`/api/v1/public/lookbook-v2/lookbook-images?${params.toString()}`);
      if (res.status === 401 || res.status === 403) {
        throw new Error('Access Denied: The provided token is invalid or has expired.');
      }
      if (!res.ok) {
        throw new Error('Failed to load lookbook images.');
      }
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to load lookbook images.');
      }
      setLookbookItems(data.items || []);
      setLookbookPagination({
        page: data.page || lookbookPage,
        limit: data.limit || 100,
        total: data.total || 0,
        totalPages: data.totalPages || 1,
        hasPrev: Boolean(data.hasPrev),
        hasNext: Boolean(data.hasNext),
      });
      setLoadedKeys((prev) => ({ ...prev, lookbook: requestKey }));
    } catch (err) {
      setLookbookError(err.message || 'An unexpected error occurred while loading lookbook images.');
    } finally {
      setLookbookLoading(false);
    }
  }, [token, lookbookPage, lookbookSkuSearch]);

  const fetchAdCreatives = useCallback(async () => {
    if (!token) {
      setAdCreativesError('Unauthorized: Access token is missing in the URL query parameters.');
      return;
    }

    setAdCreativesLoading(true);
    setAdCreativesError('');
    try {
      const params = new URLSearchParams({ token });
      const requestKey = params.toString();
      const res = await fetch(`/api/v1/public/ad-creatives?${params.toString()}`);
      if (res.status === 401 || res.status === 403) {
        throw new Error('Access Denied: The provided token is invalid or has expired.');
      }
      if (!res.ok) {
        throw new Error('Failed to load ad creatives.');
      }
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to load ad creatives.');
      }
      setAdCreatives(data.items || []);
      setLoadedKeys((prev) => ({ ...prev, adCreatives: requestKey }));
    } catch (err) {
      setAdCreativesError(err.message || 'An unexpected error occurred while loading ad creatives.');
    } finally {
      setAdCreativesLoading(false);
    }
  }, [token]);

  const fetchLiveNewArrivals = useCallback(async (forceRefresh = false) => {
    if (!token) {
      setLiveNewArrivalsError('Unauthorized: Access token is missing in the URL query parameters.');
      return;
    }

    setLiveNewArrivalsLoading(true);
    setLiveNewArrivalsError('');
    try {
      const params = new URLSearchParams({
        token,
        page: String(liveNewArrivalsPage),
        limit: '24',
      });
      if (liveNewArrivalsSkuSearch) params.set('sku', liveNewArrivalsSkuSearch);
      const requestKey = params.toString();
      if (forceRefresh) params.set('refresh', '1');
      const res = await fetch(`/api/v1/public/new-products/live-arrivals?${params.toString()}`);
      if (res.status === 401 || res.status === 403) {
        throw new Error('Access Denied: The provided token is invalid or has expired.');
      }
      if (!res.ok) {
        throw new Error('Failed to load live new arrivals.');
      }
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to load live new arrivals.');
      }
      setLiveNewArrivalItems(data.items || []);
      setLiveNewArrivalsPagination({
        page: data.page || liveNewArrivalsPage,
        limit: data.limit || 100,
        total: data.total || 0,
        totalPages: data.totalPages || 1,
        hasPrev: Boolean(data.hasPrev),
        hasNext: Boolean(data.hasNext),
      });
      setLoadedKeys((prev) => ({ ...prev, liveNewArrivals: requestKey }));
    } catch (err) {
      setLiveNewArrivalsError(err.message || 'An unexpected error occurred while loading live new arrivals.');
    } finally {
      setLiveNewArrivalsLoading(false);
    }
  }, [token, liveNewArrivalsPage, liveNewArrivalsSkuSearch]);

  const fetchScheduledNewProducts = useCallback(async () => {
    if (!token) {
      setScheduledNewProductsError('Unauthorized: Access token is missing in the URL query parameters.');
      return;
    }

    setScheduledNewProductsLoading(true);
    setScheduledNewProductsError('');
    try {
      const params = new URLSearchParams({
        token,
        page: String(scheduledNewProductsPage),
        limit: '100',
      });
      if (scheduledNewProductsSkuSearch) params.set('sku', scheduledNewProductsSkuSearch);
      const requestKey = params.toString();
      const res = await fetch(`/api/v1/public/new-products/monday-scheduled?${params.toString()}`);
      if (res.status === 401 || res.status === 403) {
        throw new Error('Access Denied: The provided token is invalid or has expired.');
      }
      if (!res.ok) {
        throw new Error('Failed to load scheduled new products.');
      }
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to load scheduled new products.');
      }
      setScheduledNewProductItems(data.items || []);
      setScheduledNewProductsPagination({
        page: data.page || scheduledNewProductsPage,
        limit: data.limit || 100,
        total: data.total || 0,
        totalPages: data.totalPages || 1,
        hasPrev: Boolean(data.hasPrev),
        hasNext: Boolean(data.hasNext),
      });
      setLoadedKeys((prev) => ({ ...prev, scheduledNewProducts: requestKey }));
    } catch (err) {
      setScheduledNewProductsError(err.message || 'An unexpected error occurred while loading scheduled new products.');
    } finally {
      setScheduledNewProductsLoading(false);
    }
  }, [token, scheduledNewProductsPage, scheduledNewProductsSkuSearch]);

  useEffect(() => {
    const params = new URLSearchParams({ token: token || '', page: String(editorialsPage), limit: '100' });
    if (editorialsSkuSearch) params.set('sku', editorialsSkuSearch);
    const requestKey = params.toString();
    if (activeTab === 'editorials' && loadedKeys.editorials !== requestKey) {
      fetchEditorials();
    }
  }, [activeTab, token, editorialsPage, editorialsSkuSearch, loadedKeys.editorials, fetchEditorials]);

  useEffect(() => {
    const params = new URLSearchParams({ token: token || '', page: String(lookbookPage), limit: '100' });
    if (lookbookSkuSearch) params.set('sku', lookbookSkuSearch);
    const requestKey = params.toString();
    if (activeTab === 'lookbook' && loadedKeys.lookbook !== requestKey) {
      fetchLookbookImages();
    }
  }, [activeTab, token, lookbookPage, lookbookSkuSearch, loadedKeys.lookbook, fetchLookbookImages]);

  useEffect(() => {
    const params = new URLSearchParams({ token: token || '' });
    const requestKey = params.toString();
    if (activeTab === 'adCreatives' && loadedKeys.adCreatives !== requestKey) {
      fetchAdCreatives();
    }
  }, [activeTab, token, loadedKeys.adCreatives, fetchAdCreatives]);

  useEffect(() => {
    const params = new URLSearchParams({ token: token || '', page: String(liveNewArrivalsPage), limit: '24' });
    if (liveNewArrivalsSkuSearch) params.set('sku', liveNewArrivalsSkuSearch);
    const requestKey = params.toString();
    if (
      activeTab === 'newProducts'
      && activeNewProductsTab === 'liveNewArrivals'
      && loadedKeys.liveNewArrivals !== requestKey
    ) {
      fetchLiveNewArrivals();
    }
  }, [activeTab, activeNewProductsTab, token, liveNewArrivalsPage, liveNewArrivalsSkuSearch, loadedKeys.liveNewArrivals, fetchLiveNewArrivals]);

  useEffect(() => {
    const params = new URLSearchParams({ token: token || '', page: String(scheduledNewProductsPage), limit: '100' });
    if (scheduledNewProductsSkuSearch) params.set('sku', scheduledNewProductsSkuSearch);
    const requestKey = params.toString();
    if (
      activeTab === 'newProducts'
      && activeNewProductsTab === 'scheduled'
      && loadedKeys.scheduledNewProducts !== requestKey
    ) {
      fetchScheduledNewProducts();
    }
  }, [activeTab, activeNewProductsTab, token, scheduledNewProductsPage, scheduledNewProductsSkuSearch, loadedKeys.scheduledNewProducts, fetchScheduledNewProducts]);

  const handleCopyLink = async (e, id, url) => {
    e.stopPropagation(); // Avoid triggering lightbox
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      alert('Failed to copy link.');
    }
  };

  const handleCopyProductUrl = async (e, id, url) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopiedProductUrlId(id);
      setTimeout(() => setCopiedProductUrlId(null), 2000);
    } catch (err) {
      alert('Failed to copy product URL.');
    }
  };

  const handleCopyAdCreativeLink = async (e, id, url) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopiedAdCreativeId(id);
      setTimeout(() => setCopiedAdCreativeId(null), 2000);
    } catch (err) {
      alert('Failed to copy link.');
    }
  };

  const handleOpenLiveProduct = (product, selectedImageUrl = '') => {
    const images = Array.isArray(product?.images) ? product.images : [];
    setLiveProductLightbox({
      ...product,
      selectedImageUrl: selectedImageUrl || images[0]?.shopifyImageUrl || images[0]?.imageUrl || product?.shopifyImageUrl || product?.imageUrl || '',
    });
  };

  const handleSelectLiveProductImage = (e, imageUrl) => {
    e.stopPropagation();
    setLiveProductLightbox((current) => current ? { ...current, selectedImageUrl: imageUrl } : current);
  };

  const handleDownload = async (e, filename, url) => {
    e.stopPropagation(); // Avoid triggering lightbox
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'editorial.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      // Fallback: Open in a new tab if blob download fails due to CORS
      window.open(url, '_blank');
    }
  };

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Failed to read image file.'));
    reader.readAsDataURL(file);
  });

  const authedFetch = useCallback(async (url, options = {}) => {
    if (!currentUser) throw new Error('Not authenticated.');
    const idToken = await currentUser.getIdToken();
    return fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${idToken}`,
        ...(options.headers || {}),
      },
    });
  }, [currentUser]);

  const handleUploadAdCreatives = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    setUploadingAdCreatives(true);
    setAdCreativesError('');
    try {
      for (const file of files) {
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
          throw new Error(`${file.name} is not a supported image type.`);
        }
        if (file.size > 15 * 1024 * 1024) {
          throw new Error(`${file.name} is larger than 15 MB.`);
        }

        const imageData = await fileToDataUrl(file);
        const res = await authedFetch('/api/v1/ad-creatives/upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageData,
            mimeType: file.type,
          }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.message || `Failed to upload ${file.name}.`);
        }
      }
      await fetchAdCreatives();
    } catch (err) {
      setAdCreativesError(err.message || 'Failed to upload ad creative.');
    } finally {
      setUploadingAdCreatives(false);
    }
  };

  const handleDeleteAdCreative = async (e, creativeId) => {
    e.stopPropagation();
    if (!window.confirm('Remove this ad creative from the shared gallery?')) return;

    setDeletingAdCreativeId(creativeId);
    setAdCreativesError('');
    try {
      const res = await authedFetch(`/api/v1/ad-creatives/${encodeURIComponent(creativeId)}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to delete ad creative.');
      }
      setAdCreatives((prev) => prev.filter((item) => item.id !== creativeId));
    } catch (err) {
      setAdCreativesError(err.message || 'Failed to delete ad creative.');
    } finally {
      setDeletingAdCreativeId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Pushed to Shopify';
    const date = new Date(dateString);
    return `Pushed ${date.toLocaleDateString()} at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const renderLiveNewArrivalsGrid = (products = []) => (
    <div className="shared-editorials-grid">
      {products.map((product) => {
        const images = Array.isArray(product.images) ? product.images : [];
        const mainImage = images[0]?.shopifyImageUrl || images[0]?.imageUrl || product.shopifyImageUrl || product.imageUrl || '';
        const filename = `new_arrival_${product.skuPrefix || product.productId || product.id}.jpg`;
        return (
          <div key={product.id} className="shared-editorial-card shared-live-product-card" onClick={() => handleOpenLiveProduct(product, mainImage)}>
            <div className="shared-editorial-image-container">
              <img src={mainImage} alt={product.productTitle || 'Live new arrival'} className="shared-editorial-img" loading="lazy" />
              <div className="shared-editorial-overlay">
                <div className="shared-editorial-actions">
                  <button
                    className={`shared-editorial-btn ${copiedId === product.id ? 'shared-editorial-btn--copied' : ''}`}
                    onClick={(e) => handleCopyLink(e, product.id, mainImage)}
                    title="Copy Image URL"
                  >
                    {copiedId === product.id ? 'Copied ✓' : 'Copy Link'}
                  </button>
                  <button
                    className="shared-editorial-btn shared-editorial-btn--download"
                    onClick={(e) => handleDownload(e, filename, mainImage)}
                    title="Download Main Image"
                  >
                    Download
                  </button>
                </div>
              </div>
            </div>
            {images.length > 1 && (
              <div className="shared-live-product-thumbs" onClick={(e) => e.stopPropagation()}>
                {images.slice(0, 8).map((image, index) => {
                  const imageUrl = image.shopifyImageUrl || image.imageUrl || '';
                  return (
                    <button
                      type="button"
                      key={image.id || `${product.id}-${index}`}
                      className="shared-live-product-thumb"
                      onClick={() => handleOpenLiveProduct(product, imageUrl)}
                      title={`Open image ${index + 1}`}
                    >
                      <img src={imageUrl} alt={`${product.productTitle || 'Product'} ${index + 1}`} loading="lazy" />
                    </button>
                  );
                })}
                {images.length > 8 && (
                  <button
                    type="button"
                    className="shared-live-product-thumb shared-live-product-thumb--more"
                    onClick={() => handleOpenLiveProduct(product, mainImage)}
                    title="Open all images"
                  >
                    +{images.length - 8}
                  </button>
                )}
              </div>
            )}
            <div className="shared-editorial-info">
              <div className="shared-editorial-meta">
                <span className="shared-editorial-sku">{product.skuPrefix || 'No SKU'}</span>
                <span className="shared-editorial-fabric">{product.fabric}</span>
              </div>
              <h3 className="shared-editorial-title">{product.productTitle || 'New Arrival Product'}</h3>
              <div className="shared-editorial-date">Live new arrival · {images.length} image{images.length === 1 ? '' : 's'}</div>
              {product.productUrl && (
                <div className="shared-editorial-store-actions" onClick={(e) => e.stopPropagation()}>
                  <a
                    href={product.productUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shared-editorial-store-link"
                  >
                    Visit Store ↗
                  </a>
                  <button
                    className={`shared-editorial-store-copy-btn ${copiedProductUrlId === product.id ? 'shared-editorial-store-copy-btn--copied' : ''}`}
                    onClick={(e) => handleCopyProductUrl(e, product.id, product.productUrl)}
                    title="Copy Product storefront URL"
                  >
                    {copiedProductUrlId === product.id ? 'Copied URL ✓' : 'Copy URL'}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderLookbookGrid = (gridItems = []) => (
    <div className="shared-editorials-grid">
      {gridItems.map((item) => {
        const safeFabric = String(item.fabric || 'lookbook').trim() || 'lookbook';
        const filename = `${safeFabric.replace(/\s+/g, '_')}_${item.skuPrefix || item.id}.jpg`;
        const statusText = item.section === 'next_monday_scheduled'
          ? 'Next Monday scheduled'
          : (item.section === 'live_new_arrivals' ? 'Live new arrival' : formatDate(item.publishedAt));
        return (
          <div key={item.id} className="shared-editorial-card" onClick={() => setLightboxUrl(item.shopifyImageUrl)}>
            <div className="shared-editorial-image-container">
              <img src={item.shopifyImageUrl} alt={item.fabric || 'Lookbook image'} className="shared-editorial-img" loading="lazy" />
              <div className="shared-editorial-overlay">
                <div className="shared-editorial-actions">
                  <button
                    className={`shared-editorial-btn ${copiedId === item.id ? 'shared-editorial-btn--copied' : ''}`}
                    onClick={(e) => handleCopyLink(e, item.id, item.shopifyImageUrl)}
                    title="Copy Shopify Image URL"
                  >
                    {copiedId === item.id ? 'Copied ✓' : 'Copy Link'}
                  </button>
                  <button
                    className="shared-editorial-btn shared-editorial-btn--download"
                    onClick={(e) => handleDownload(e, filename, item.shopifyImageUrl)}
                    title="Download Image"
                  >
                    Download
                  </button>
                </div>
              </div>
            </div>
            <div className="shared-editorial-info">
              <div className="shared-editorial-meta">
                <span className="shared-editorial-sku">{item.skuPrefix || 'No SKU'}</span>
                <span className="shared-editorial-fabric">{item.fabric}</span>
              </div>
              <h3 className="shared-editorial-title">{item.productTitle || 'Fabric Product'}</h3>
              {(item.batchNumber || item.designNumber || item.style) && (
                <div className="shared-editorial-submeta">
                  {[item.batchNumber ? `Batch ${item.batchNumber}` : '', item.designNumber ? `Design ${item.designNumber}` : '', item.style ? item.style.replace(/_/g, ' ') : ''].filter(Boolean).join(' · ')}
                </div>
              )}
              <div className="shared-editorial-date">{statusText}</div>
              {item.productUrl && (
                <div className="shared-editorial-store-actions" onClick={(e) => e.stopPropagation()}>
                  <a
                    href={item.productUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shared-editorial-store-link"
                  >
                    Visit Store ↗
                  </a>
                  <button
                    className={`shared-editorial-store-copy-btn ${copiedProductUrlId === item.id ? 'shared-editorial-store-copy-btn--copied' : ''}`}
                    onClick={(e) => handleCopyProductUrl(e, item.id, item.productUrl)}
                    title="Copy Product storefront URL"
                  >
                    {copiedProductUrlId === item.id ? 'Copied URL ✓' : 'Copy URL'}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderPagination = (pagination, setPage, isLoading, itemLabel = 'images') => {
    if (!pagination || pagination.totalPages <= 1) return null;
    return (
      <div className="shared-editorials-pagination">
        <button
          className="shared-editorials-page-btn"
          onClick={() => setPage((value) => Math.max(1, value - 1))}
          disabled={!pagination.hasPrev || isLoading}
        >
          Previous
        </button>
        <div className="shared-editorials-page-status">
          Page {pagination.page} of {pagination.totalPages}
          <span>{pagination.total} {itemLabel}</span>
        </div>
        <button
          className="shared-editorials-page-btn"
          onClick={() => setPage((value) => value + 1)}
          disabled={!pagination.hasNext || isLoading}
        >
          Next
        </button>
      </div>
    );
  };

  const renderSkuSearch = ({
    inputValue,
    searchValue,
    onInputChange,
    onSearch,
    onClear,
    disabled,
  }) => (
    <form
      className="shared-editorials-search"
      onSubmit={(e) => {
        e.preventDefault();
        onSearch();
      }}
    >
      <input
        type="search"
        value={inputValue}
        onChange={(e) => onInputChange(e.target.value)}
        placeholder="Search SKU"
        disabled={disabled}
      />
      <button type="submit" disabled={disabled}>
        Search
      </button>
      {searchValue && (
        <button type="button" onClick={onClear} disabled={disabled}>
          Clear
        </button>
      )}
    </form>
  );

  if (!token) {
    return (
      <div className="shared-editorials-error-page">
        <div className="shared-editorials-error-card">
          <h2>Access Denied</h2>
          <p>You need a valid secure token to view the Fabric Editorial Hub.</p>
          <p className="hint">Please contact your administrator for the shareable catalog link.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="shared-editorials-container">
      <div className="shared-editorials-header">
        <div>
          <h1>Fabric Creative Hub</h1>
        </div>
      </div>

      <div className="shared-editorials-tabs" role="tablist" aria-label="Creative sections">
        <button
          type="button"
          className={`shared-editorials-tab ${activeTab === 'editorials' ? 'shared-editorials-tab--active' : ''}`}
          onClick={() => setActiveTab('editorials')}
          role="tab"
          aria-selected={activeTab === 'editorials'}
        >
          Editorials
        </button>
        <button
          type="button"
          className={`shared-editorials-tab ${activeTab === 'lookbook' ? 'shared-editorials-tab--active' : ''}`}
          onClick={() => setActiveTab('lookbook')}
          role="tab"
          aria-selected={activeTab === 'lookbook'}
        >
          Lookbook
        </button>
        <button
          type="button"
          className={`shared-editorials-tab ${activeTab === 'newProducts' ? 'shared-editorials-tab--active' : ''}`}
          onClick={() => setActiveTab('newProducts')}
          role="tab"
          aria-selected={activeTab === 'newProducts'}
        >
          New Products
        </button>
        <button
          type="button"
          className={`shared-editorials-tab ${activeTab === 'adCreatives' ? 'shared-editorials-tab--active' : ''}`}
          onClick={() => setActiveTab('adCreatives')}
          role="tab"
          aria-selected={activeTab === 'adCreatives'}
        >
          Ad Creatives
        </button>
      </div>

      {activeTab === 'editorials' && (
        <section className="shared-editorials-tab-panel" role="tabpanel">
          <div className="shared-editorials-section-header shared-editorials-section-header--top">
            <div>
              <h2>Fabric Editorial Catalog</h2>
              {editorialsSkuSearch && (
                <p className="shared-editorials-search-status">Showing SKU results for {editorialsSkuSearch}</p>
              )}
            </div>
            <div className="shared-editorials-header-actions">
              {renderSkuSearch({
                inputValue: editorialsSkuInput,
                searchValue: editorialsSkuSearch,
                onInputChange: setEditorialsSkuInput,
                onSearch: () => {
                  setEditorialsSkuSearch(editorialsSkuInput.trim());
                  setEditorialsPage(1);
                },
                onClear: () => {
                  setEditorialsSkuInput('');
                  setEditorialsSkuSearch('');
                  setEditorialsPage(1);
                },
                disabled: loading,
              })}
              <button className="shared-editorials-refresh-btn" onClick={fetchEditorials} disabled={loading}>
                {loading ? 'Refreshing...' : 'Refresh Feed'}
              </button>
            </div>
          </div>

          {error ? (
            <div className="shared-editorials-error-card">
              <h2>Failed to Load</h2>
              <p>{error}</p>
            </div>
          ) : loading ? (
            <div className="shared-editorials-loader">
              <div className="shared-editorials-spinner"></div>
              <p>Fetching...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="shared-editorials-empty">
              <p>{editorialsSkuSearch ? 'No editorial images found for this SKU.' : 'No published editorials found in the system catalog.'}</p>
            </div>
          ) : (
            <>
              {renderLookbookGrid(items)}
              {renderPagination(editorialsPagination, setEditorialsPage, loading)}
            </>
          )}
        </section>
      )}

      {activeTab === 'lookbook' && (
        <section className="shared-editorials-tab-panel shared-editorials-section--lookbook" role="tabpanel">
          <div className="shared-editorials-section-header shared-editorials-section-header--top">
            <div>
              <h2>Lookbook Images</h2>
              {lookbookSkuSearch && (
                <p className="shared-editorials-search-status">Showing SKU results for {lookbookSkuSearch}</p>
              )}
            </div>
            {renderSkuSearch({
              inputValue: lookbookSkuInput,
              searchValue: lookbookSkuSearch,
              onInputChange: setLookbookSkuInput,
              onSearch: () => {
                setLookbookSkuSearch(lookbookSkuInput.trim());
                setLookbookPage(1);
              },
              onClear: () => {
                setLookbookSkuInput('');
                setLookbookSkuSearch('');
                setLookbookPage(1);
              },
              disabled: lookbookLoading,
            })}
          </div>

          {lookbookError ? (
            <div className="shared-editorials-error-card shared-editorials-error-card--inline">
              <h2>Failed to Load</h2>
              <p>{lookbookError}</p>
            </div>
          ) : lookbookLoading ? (
            <div className="shared-editorials-loader shared-editorials-loader--compact">
              <div className="shared-editorials-spinner"></div>
              <p>Fetching lookbook images...</p>
            </div>
          ) : lookbookItems.length === 0 ? (
            <div className="shared-editorials-empty shared-editorials-empty--compact">
              <p>{lookbookSkuSearch ? 'No lookbook images found for this SKU.' : 'No published lookbook images found in the system catalog.'}</p>
            </div>
          ) : (
            <>
              {renderLookbookGrid(lookbookItems)}
              {renderPagination(lookbookPagination, setLookbookPage, lookbookLoading)}
            </>
          )}
        </section>
      )}

      {activeTab === 'newProducts' && (
        <section className="shared-editorials-tab-panel shared-editorials-section--new-products" role="tabpanel">
          <div className="shared-editorials-section-header shared-editorials-section-header--top">
            <div>
              <h2>New Products</h2>
            </div>
          </div>

          <div className="shared-editorials-subtabs" role="tablist" aria-label="New product sections">
            <button
              type="button"
              className={`shared-editorials-subtab ${activeNewProductsTab === 'liveNewArrivals' ? 'shared-editorials-subtab--active' : ''}`}
              onClick={() => setActiveNewProductsTab('liveNewArrivals')}
              role="tab"
              aria-selected={activeNewProductsTab === 'liveNewArrivals'}
            >
              Live New Arrivals
            </button>
            <button
              type="button"
              className={`shared-editorials-subtab ${activeNewProductsTab === 'scheduled' ? 'shared-editorials-subtab--active' : ''}`}
              onClick={() => setActiveNewProductsTab('scheduled')}
              role="tab"
              aria-selected={activeNewProductsTab === 'scheduled'}
            >
              Next Monday Scheduled
            </button>
          </div>

          {activeNewProductsTab === 'liveNewArrivals' && (
            <div className="shared-editorials-subtab-panel" role="tabpanel">
              <div className="shared-editorials-section-header shared-editorials-section-header--nested">
                <div>
                  <h2>Live New Arrivals</h2>
                  {liveNewArrivalsSkuSearch && (
                    <p className="shared-editorials-search-status">Showing SKU results for {liveNewArrivalsSkuSearch}</p>
                  )}
                </div>
                <div className="shared-editorials-header-actions">
                  {renderSkuSearch({
                    inputValue: liveNewArrivalsSkuInput,
                    searchValue: liveNewArrivalsSkuSearch,
                    onInputChange: setLiveNewArrivalsSkuInput,
                    onSearch: () => {
                      setLiveNewArrivalsSkuSearch(liveNewArrivalsSkuInput.trim());
                      setLiveNewArrivalsPage(1);
                    },
                    onClear: () => {
                      setLiveNewArrivalsSkuInput('');
                      setLiveNewArrivalsSkuSearch('');
                      setLiveNewArrivalsPage(1);
                    },
                    disabled: liveNewArrivalsLoading,
                  })}
                  <button className="shared-editorials-refresh-btn" onClick={() => fetchLiveNewArrivals(true)} disabled={liveNewArrivalsLoading}>
                    {liveNewArrivalsLoading ? 'Refreshing...' : 'Refresh'}
                  </button>
                </div>
              </div>

              {liveNewArrivalsError ? (
                <div className="shared-editorials-error-card shared-editorials-error-card--inline">
                  <h2>Failed to Load</h2>
                  <p>{liveNewArrivalsError}</p>
                </div>
              ) : liveNewArrivalsLoading ? (
                <div className="shared-editorials-loader shared-editorials-loader--compact">
                  <div className="shared-editorials-spinner"></div>
                  <p>Fetching live new arrivals...</p>
                </div>
              ) : liveNewArrivalItems.length === 0 ? (
                <div className="shared-editorials-empty shared-editorials-empty--compact">
                  <p>{liveNewArrivalsSkuSearch ? 'No live new arrival images found for this SKU.' : 'No live new arrival images found.'}</p>
                </div>
              ) : (
                <>
                  {renderLiveNewArrivalsGrid(liveNewArrivalItems)}
                  {renderPagination(liveNewArrivalsPagination, setLiveNewArrivalsPage, liveNewArrivalsLoading, 'products')}
                </>
              )}
            </div>
          )}

          {activeNewProductsTab === 'scheduled' && (
            <div className="shared-editorials-subtab-panel" role="tabpanel">
              <div className="shared-editorials-section-header shared-editorials-section-header--nested">
                <div>
                  <h2>Next Monday Scheduled</h2>
                  {scheduledNewProductsSkuSearch && (
                    <p className="shared-editorials-search-status">Showing SKU results for {scheduledNewProductsSkuSearch}</p>
                  )}
                </div>
                <div className="shared-editorials-header-actions">
                  {renderSkuSearch({
                    inputValue: scheduledNewProductsSkuInput,
                    searchValue: scheduledNewProductsSkuSearch,
                    onInputChange: setScheduledNewProductsSkuInput,
                    onSearch: () => {
                      setScheduledNewProductsSkuSearch(scheduledNewProductsSkuInput.trim());
                      setScheduledNewProductsPage(1);
                    },
                    onClear: () => {
                      setScheduledNewProductsSkuInput('');
                      setScheduledNewProductsSkuSearch('');
                      setScheduledNewProductsPage(1);
                    },
                    disabled: scheduledNewProductsLoading,
                  })}
                  <button className="shared-editorials-refresh-btn" onClick={fetchScheduledNewProducts} disabled={scheduledNewProductsLoading}>
                    {scheduledNewProductsLoading ? 'Refreshing...' : 'Refresh'}
                  </button>
                </div>
              </div>

              {scheduledNewProductsError ? (
                <div className="shared-editorials-error-card shared-editorials-error-card--inline">
                  <h2>Failed to Load</h2>
                  <p>{scheduledNewProductsError}</p>
                </div>
              ) : scheduledNewProductsLoading ? (
                <div className="shared-editorials-loader shared-editorials-loader--compact">
                  <div className="shared-editorials-spinner"></div>
                  <p>Fetching scheduled new products...</p>
                </div>
              ) : scheduledNewProductItems.length === 0 ? (
                <div className="shared-editorials-empty shared-editorials-empty--compact">
                  <p>{scheduledNewProductsSkuSearch ? 'No scheduled new product images found for this SKU.' : 'No products are currently scheduled for next Monday.'}</p>
                </div>
              ) : (
                <>
                  {renderLookbookGrid(scheduledNewProductItems)}
                  {renderPagination(scheduledNewProductsPagination, setScheduledNewProductsPage, scheduledNewProductsLoading)}
                </>
              )}
            </div>
          )}
        </section>
      )}

      {activeTab === 'adCreatives' && (
        <section className="shared-editorials-tab-panel shared-editorials-section--ad-creatives" role="tabpanel">
          <div className="shared-editorials-section-header shared-editorials-section-header--top">
            <h2>Ad Creatives</h2>
            {isAdmin && (
              <label className={`shared-editorials-upload-btn ${uploadingAdCreatives ? 'shared-editorials-upload-btn--disabled' : ''}`}>
                {uploadingAdCreatives ? 'Uploading...' : 'Upload Ad Creative'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handleUploadAdCreatives}
                  disabled={uploadingAdCreatives}
                />
              </label>
            )}
          </div>

          {adCreativesError && (
            <div className="shared-editorials-error-card shared-editorials-error-card--inline">
              <h2>Ad Creatives Error</h2>
              <p>{adCreativesError}</p>
            </div>
          )}

          {adCreativesLoading ? (
            <div className="shared-editorials-loader shared-editorials-loader--compact">
              <div className="shared-editorials-spinner"></div>
              <p>Fetching ad creatives...</p>
            </div>
          ) : adCreatives.length === 0 ? (
            <div className="shared-editorials-empty shared-editorials-empty--compact">
              <p>No ad creatives uploaded yet.</p>
            </div>
          ) : (
            <div className="shared-editorials-grid">
              {adCreatives.map((creative, index) => {
                const filename = `ad_creative_${index + 1}.jpg`;
                return (
                  <div key={creative.id} className="shared-editorial-card" onClick={() => setLightboxUrl(creative.imageUrl)}>
                    <div className="shared-editorial-image-container">
                      <img src={creative.imageUrl} alt="Ad creative" className="shared-editorial-img" loading="lazy" />
                      <div className="shared-editorial-overlay">
                        <div className="shared-editorial-actions shared-editorial-actions--wrap">
                          <button
                            className={`shared-editorial-btn ${copiedAdCreativeId === creative.id ? 'shared-editorial-btn--copied' : ''}`}
                            onClick={(e) => handleCopyAdCreativeLink(e, creative.id, creative.imageUrl)}
                            title="Copy Ad Creative Image URL"
                          >
                            {copiedAdCreativeId === creative.id ? 'Copied ✓' : 'Copy Link'}
                          </button>
                          <button
                            className="shared-editorial-btn shared-editorial-btn--download"
                            onClick={(e) => handleDownload(e, filename, creative.imageUrl)}
                            title="Download Image"
                          >
                            Download
                          </button>
                          {isAdmin && (
                            <button
                              className="shared-editorial-btn shared-editorial-btn--danger"
                              onClick={(e) => handleDeleteAdCreative(e, creative.id)}
                              disabled={deletingAdCreativeId === creative.id}
                              title="Remove Ad Creative"
                            >
                              {deletingAdCreativeId === creative.id ? 'Removing...' : 'Remove'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {liveProductLightbox && (
        <div className="shared-live-product-lightbox" onClick={() => setLiveProductLightbox(null)}>
          <div className="shared-live-product-modal" onClick={(e) => e.stopPropagation()}>
            <button className="shared-editorial-lightbox-close" onClick={() => setLiveProductLightbox(null)}>✕</button>
            <div className="shared-live-product-modal-media">
              <img
                src={liveProductLightbox.selectedImageUrl}
                alt={liveProductLightbox.productTitle || 'Live new arrival'}
                className="shared-live-product-modal-img"
              />
            </div>
            <div className="shared-live-product-modal-info">
              <div className="shared-editorial-meta">
                <span className="shared-editorial-sku">{liveProductLightbox.skuPrefix || 'No SKU'}</span>
                <span className="shared-editorial-fabric">{liveProductLightbox.fabric}</span>
              </div>
              <h2>{liveProductLightbox.productTitle || 'New Arrival Product'}</h2>
              <div className="shared-live-product-modal-actions">
                <button
                  className={`shared-editorial-btn ${copiedId === `${liveProductLightbox.id}-modal` ? 'shared-editorial-btn--copied' : ''}`}
                  onClick={(e) => handleCopyLink(e, `${liveProductLightbox.id}-modal`, liveProductLightbox.selectedImageUrl)}
                >
                  {copiedId === `${liveProductLightbox.id}-modal` ? 'Copied ✓' : 'Copy Image'}
                </button>
                <button
                  className="shared-editorial-btn shared-editorial-btn--download"
                  onClick={(e) => handleDownload(e, `new_arrival_${liveProductLightbox.skuPrefix || liveProductLightbox.productId || 'image'}.jpg`, liveProductLightbox.selectedImageUrl)}
                >
                  Download
                </button>
                {liveProductLightbox.productUrl && (
                  <a
                    href={liveProductLightbox.productUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shared-editorial-store-link shared-live-product-modal-store-link"
                  >
                    Visit Store ↗
                  </a>
                )}
              </div>
              <div className="shared-live-product-modal-thumbs">
                {(liveProductLightbox.images || []).map((image, index) => {
                  const imageUrl = image.shopifyImageUrl || image.imageUrl || '';
                  return (
                    <button
                      type="button"
                      key={image.id || `${liveProductLightbox.id}-modal-${index}`}
                      className={`shared-live-product-modal-thumb ${liveProductLightbox.selectedImageUrl === imageUrl ? 'shared-live-product-modal-thumb--active' : ''}`}
                      onClick={(e) => handleSelectLiveProductImage(e, imageUrl)}
                    >
                      <img src={imageUrl} alt={`${liveProductLightbox.productTitle || 'Product'} ${index + 1}`} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {lightboxUrl && (
        <div className="shared-editorial-lightbox" onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="Fullscreen preview" className="shared-editorial-lightbox-img" />
          <button className="shared-editorial-lightbox-close" onClick={() => setLightboxUrl(null)}>✕</button>
        </div>
      )}
    </div>
  );
}
